import { useEffect, useState } from "react";
import { useLanguage } from "@/lib/language-context";
import type { Language } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Globe, CheckCircle2 } from "lucide-react";

const FIRST_VISIT_KEY = "vimana-first-visit-completed";

export function LanguageSelector() {
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Check if user has visited before
    try {
      const hasVisited = localStorage.getItem(FIRST_VISIT_KEY);
      if (!hasVisited) {
        // Check if language is already explicitly set (from previous session)
        const savedLanguage = localStorage.getItem("vimana-language");
        if (!savedLanguage) {
          // First visit and no language explicitly selected - show modal
          setIsOpen(true);
        } else {
          // Language already explicitly selected in previous session, mark as visited
          localStorage.setItem(FIRST_VISIT_KEY, "true");
        }
      }
    } catch {
      // localStorage not available, don't show modal
    }
  }, []);

  const handleLanguageSelect = (lang: Language) => {
    setLanguage(lang);
    try {
      localStorage.setItem(FIRST_VISIT_KEY, "true");
    } catch {
      // localStorage not available
    }
    setIsOpen(false);
  };

  // Don't render until mounted to avoid hydration issues
  if (!mounted) {
    return null;
  }

  const languageLabels: Record<Language, string> = {
    en: "English",
    si: "සිංහල",
    ta: "தமிழ்",
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent 
        className="max-w-md" 
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex flex-col items-center text-center mb-4">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Globe className="h-10 w-10 text-primary" />
            </div>
            <DialogTitle className="text-2xl font-bold text-foreground mb-2">
              {t.language || 'Select Language'}
            </DialogTitle>
            <DialogDescription className="text-base">
              {t.selectLanguageDesc || 'Please choose your preferred language for the application'}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-4">
          {(["en", "si", "ta"] as Language[]).map((lang) => (
            <button
              key={lang}
              onClick={() => handleLanguageSelect(lang)}
              className={`w-full p-6 rounded-xl border-2 transition-all text-left ${
                language === lang
                  ? "bg-primary text-primary-foreground border-primary shadow-lg"
                  : "bg-card text-foreground border-border hover:border-primary/50 hover:bg-secondary"
              }`}
              aria-label={languageLabels[lang]}
              aria-pressed={language === lang}
            >
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold">{languageLabels[lang]}</span>
                {language === lang && (
                  <CheckCircle2 className="h-6 w-6" />
                )}
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
